import React from 'react';
import { styled } from '@stitches/react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// 英雄区块容器样式
const HeroContainer = styled('section', {
  width: '100%',
  minHeight: '60vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'url("/images/bg1.jpg")', // 使用提供的背景图片
  backgroundSize: 'cover', // 确保图片覆盖整个区域
  backgroundPosition: 'center', // 居中显示图片
  color: 'white',
  padding: '60px',
  position: 'relative',
  overflow: 'hidden',

  '@media (max-width: 768px)': {
    minHeight: '80vh', // 减少高度
    padding: '0', // 移除内边距
    alignItems: 'flex-start', // 改为顶部对齐
    paddingTop: '60px', // 为顶部导航栏留出空间
    background: 'url("/images/bg1.jpg")', // 在移动端使用相同的背景图片
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  }
});

// 星空背景 - 降低不透明度使其与新背景图片融合
const StarsBackground = styled('div', {
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  zIndex: 0,
  opacity: 0.4, // 降低不透明度，使其不会完全覆盖背景图片
  backgroundImage: `
    radial-gradient(2px 2px at 20px 30px, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0)),
    radial-gradient(2px 2px at 40px 70px, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0)),
    radial-gradient(3px 3px at 120px 40px, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0)),
    radial-gradient(1px 1px at 200px 90px, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0)),
    radial-gradient(2px 2px at 300px 60px, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0))
  `,
  backgroundRepeat: 'repeat',
  backgroundSize: '400px 400px',

  '@media (max-width: 768px)': {
    backgroundSize: '250px 250px', // 调整背景大小，适合小屏幕
    opacity: 0.4 // 保持与桌面版相同的不透明度
  }
});

// 背景遮罩 - 增加可读性
const BackgroundOverlay = styled('div', {
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  zIndex: 0,
  background: 'rgba(30, 41, 59, 0.5)', // 深蓝色半透明遮罩

  '@media (max-width: 768px)': {
    background: 'rgba(34, 42, 60, 0.5)' // 移动端调整透明度让背景更明显
  }
});

// 内容容器
const ContentContainer = styled('div', {
  maxWidth: 'var(--container-width)',
  width: '100%',
  padding: '0 var(--spacing-xl)',
  paddingLeft: '110px', // 恢复左侧间距，与导航栏保持一致
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start', // 改为左对齐
  justifyContent: 'center',
  position: 'relative',
  zIndex: 3,
  marginLeft: 'auto',
  marginRight: 'auto',

  '@media (max-width: 768px)': {
    padding: '0 20px',
    paddingLeft: '20px', // 移动端统一左侧间距
    marginTop: '0',
    alignItems: 'center', // 移动端居中对齐
    justifyContent: 'flex-start', // 内容顶部对齐
    minHeight: 'auto', // 自动高度
    paddingTop: '20px', // 减少顶部间距
    paddingBottom: '40px' // 底部间距
  }
});

// 文本内容区
const TextContent = styled('div', {
  maxWidth: '800px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start', // 改为左对齐

  '@media (max-width: 768px)': {
    marginTop: '20px', // 减少顶部间距
    padding: '0',
    alignItems: 'center', // 移动端居中对齐
    textAlign: 'center',
    width: '100%'
  }
});

// 标题
const Title = styled('h1', {
  fontSize: 'var(--font-size-display)',
  fontWeight: 'var(--font-weight-bold)',
  marginBottom: 'var(--spacing-lg)',
  lineHeight: 1.2,
  color: 'white',
  textAlign: 'left', // 改为左对齐

  '& .mobile-only': {
    '@media (min-width: 769px)': {
      display: 'none'
    }
  },

  '& .pc-only': {
    '@media (max-width: 768px)': {
      display: 'none'
    }
  },

  '@media (max-width: 768px)': {
    fontSize: '36px',
    marginBottom: '1px',
    lineHeight: 1.1,
    fontWeight: '700',
    textAlign: 'center', // 移动端居中对齐
    '& .mobile-break': {
      display: 'block'
    },
    '& .subtitle': {
      display: 'block',
      fontSize: '24px',
      marginTop: '1px'
    }
  },

  '@media (min-width: 769px)': {
    fontSize: '52px',
    '& .mobile-break': {
      display: 'block'
    },
    '& .subtitle': {
      display: 'block',
      fontSize: 'inherit',
      marginTop: '5px'
    }
  }
});



// 特性列表
const FeatureList = styled('ul', {
  listStyle: 'none',
  marginBottom: 'var(--spacing-xl)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-md)',
  alignItems: 'flex-start', // 改为左对齐

  '@media (max-width: 768px)': {
    marginBottom: '20px',
    gap: '8px',
    marginTop: '15px',
    alignItems: 'center', // 移动端居中对齐
    textAlign: 'center'
  }
});

// 特性项
const FeatureItem = styled('li', {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-sm)',
  fontSize: 'var(--font-size-md)',
  color: 'white',
  position: 'relative',
  paddingLeft: '20px',

  '@media (max-width: 768px)': {
    fontSize: '16px',
    paddingLeft: '16px',
    marginBottom: '0',
    lineHeight: 1.4,
    justifyContent: 'center', // 移动端居中对齐
    textAlign: 'center'
  },

  '@media (min-width: 769px)': {
    fontSize: '18px',
  },

  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'inline-block',
    width: '0',
    height: '0',
    borderTop: '6px solid transparent',
    borderBottom: '6px solid transparent',
    borderLeft: '10px solid #FFD166',
    marginRight: 'var(--spacing-xs)',

    '@media (max-width: 768px)': {
      borderTop: '4px solid transparent',
      borderBottom: '4px solid transparent',
      borderLeft: '8px solid #FFD166',
      left: '-10px' // 调整箭头位置以适应居中对齐
    }
  }
});

// 按钮组
const ButtonGroup = styled('div', {
  display: 'flex',
  gap: 'var(--spacing-md)',
  marginTop: 'calc(var(--spacing-xl) + 100px)', // 增加100px向下移动
  justifyContent: 'flex-start', // 改为左对齐

  '@media (max-width: 768px)': {
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    maxWidth: '400px', // 限制最大宽度
    marginTop: '300px', // 300px
    justifyContent: 'center',
    alignItems: 'center'
  }
});

// 主按钮 - 黄色
const PrimaryButton = styled(Link, {
  display: 'inline-block',
  padding: 'var(--spacing-md) var(--spacing-xl)',
  backgroundColor: '#FFD166',
  color: '#1E293B',
  textDecoration: 'none',
  fontWeight: 'var(--font-weight-bold)',
  borderRadius: 'var(--border-radius-md)',
  transition: 'all var(--transition-fast)',
  boxShadow: 'var(--shadow-md)',
  fontSize: '18px',
  textAlign: 'center',
  minWidth: '140px',

  '@media (max-width: 768px)': {
    width: '100%',
    padding: '12px 0',
    fontSize: '16px',
    fontWeight: '600'
  },

  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: 'var(--shadow-lg)',
    backgroundColor: '#FFE57F'
  }
});

// 次要按钮
const SecondaryButton = styled(Link, {
  display: 'inline-block',
  padding: 'var(--spacing-md) var(--spacing-xl)',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(8px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  color: 'white',
  textDecoration: 'none',
  fontWeight: 'var(--font-weight-bold)',
  borderRadius: 'var(--border-radius-md)',
  transition: 'all var(--transition-fast)',
  fontSize: '18px',
  textAlign: 'center',
  minWidth: '140px',

  '@media (max-width: 768px)': {
    width: '100%',
    padding: '12px 0',
    fontSize: '16px',
    fontWeight: '600'
  },

  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: 'var(--shadow-md)',
    backgroundColor: 'rgba(255, 255, 255, 0.15)'
  }
});

/**
 * 首页英雄区块
 */
const HeroSection: React.FC = () => {
  const { t } = useTranslation(); // 使用国际化钩子

  return (
    <HeroContainer>
      {/* 背景星空效果 */}
      <StarsBackground />

      {/* 背景遮罩 */}
      <BackgroundOverlay />

      {/* 主内容区 */}
      <ContentContainer>
        <TextContent>
          <Title>
            <span className="mobile-break">{t('home.hero.title')}</span>
            <span className="mobile-break">{t('home.hero.subtitle')}</span>
          </Title>

          <FeatureList>
            <FeatureItem>{t('home.hero.feature1')}</FeatureItem>
            <FeatureItem>{t('home.hero.feature2')}</FeatureItem>
          </FeatureList>

          <ButtonGroup>
            <PrimaryButton as="a" href="http://nft.bitengine.org/shop" target="_blank" rel="noopener noreferrer">{t('home.hero.buyMiner')}</PrimaryButton>
            <SecondaryButton as="a" href="https://biteng.gitbook.io/biteng" target="_blank" rel="noopener noreferrer">{t('home.hero.whitepaper')}</SecondaryButton>
          </ButtonGroup>
        </TextContent>
      </ContentContainer>
    </HeroContainer>
  );
};

export default HeroSection; 